import { useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { MapPin, Calendar, ArrowRight, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Badge } from "../ui/badge";

const categoryColors = {
  "Neubau": "bg-komel-newbuild",
  "Sanierung": "bg-komel-renovation",
  "Renovation": "bg-komel-refurbishment"
};

const DEFAULT_IMAGE = "https://images.pexels.com/photos/36065437/pexels-photo-36065437.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Helper to get cover image
const getCoverImage = (ref) => {
  if (ref.images && ref.images.length > 0) {
    const index = ref.coverImageIndex || 0;
    return ref.images[index] || ref.images[0];
  }
  // Fallback for old data structure
  return ref.image || DEFAULT_IMAGE;
};

// Image Carousel Component
const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="relative aspect-video">
        <img src={DEFAULT_IMAGE} alt={title} className="w-full h-full object-cover" />
      </div>
    );
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="relative aspect-video bg-komel-neutral-100">
      <AnimatePresence mode="wait">
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`${title} - Bild ${currentIndex + 1}`}
          className="w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        />
      </AnimatePresence>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-6 h-6 text-komel-primary" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-6 h-6 text-komel-primary" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                idx === currentIndex ? "bg-white w-6" : "bg-white/50 hover:bg-white/70"
              }`}
              data-testid={`carousel-dot-${idx}`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 right-4 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

const ReferencesSection = ({ references }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [selectedRef, setSelectedRef] = useState(null);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="referenzen" 
      className="py-24 md:py-32 bg-white"
      data-testid="references-section"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-komel-primary mb-4" data-testid="references-title">
            Referenzen
          </h2>
          <div className="section-divider" />
          <p className="text-lg text-komel-secondary max-w-2xl mx-auto">
            Ein Auszug unseren Projekten.
          </p>
        </motion.div>

        {/* References Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {references.map((ref, index) => (
            <motion.div
              key={ref.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onClick={() => setSelectedRef(ref)}
              className="reference-card group cursor-pointer overflow-hidden shadow-md hover:shadow-2xl"
              data-testid={`reference-card-${index}`}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img 
                  src={getCoverImage(ref)} 
                  alt={ref.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="reference-overlay absolute inset-0 bg-gradient-to-t from-komel-primary/90 via-komel-primary/40 to-transparent opacity-0 transition-opacity duration-300 flex items-end p-6">
                  <div className="flex items-center gap-2 text-white">
                    <span className="font-medium">Details ansehen</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <Badge className={`${categoryColors[ref.category] || 'bg-komel-primary'} text-white font-medium px-3 py-1`}>
                    {ref.category}
                  </Badge>
                </div>

                {/* Image count indicator */}
                {ref.images && ref.images.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                    {ref.images.length} Bilder
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6 bg-white">
                <h3 className="font-heading text-xl font-semibold text-komel-primary mb-2">
                  {ref.title}
                </h3>
                <div className="flex items-center gap-4 text-sm text-komel-secondary mb-3">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {ref.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {ref.year}
                  </span>
                </div>
                <p className="text-komel-secondary text-sm line-clamp-2">
                  {ref.shortDescription}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <Button
            onClick={() => scrollToSection("#kontakt")}
            className="bg-komel-primary hover:bg-komel-primary-light text-white font-semibold px-10 py-6 text-lg rounded-none transition-all duration-300 hover:scale-105"
            data-testid="references-cta"
          >
            Projekt besprechen
          </Button>
        </motion.div>
      </div>

      {/* Reference Detail Modal with Carousel */}
      <Dialog open={!!selectedRef} onOpenChange={() => setSelectedRef(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="reference-modal">
          {selectedRef && (
            <>
              {/* Modal Image Carousel */}
              <div className="relative">
                <ImageCarousel 
                  images={selectedRef.images || (selectedRef.image ? [selectedRef.image] : [])} 
                  title={selectedRef.title}
                />
                <div className="absolute top-4 left-4">
                  <Badge className={`${categoryColors[selectedRef.category] || 'bg-komel-primary'} text-white font-medium px-4 py-1.5`}>
                    {selectedRef.category}
                  </Badge>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-8">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl md:text-3xl font-bold text-komel-primary mb-4">
                    {selectedRef.title}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Projektdetails für {selectedRef.title}
                  </DialogDescription>
                </DialogHeader>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 mb-6 text-komel-secondary">
                  <span className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    {selectedRef.location}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {selectedRef.year}
                  </span>
                </div>

                {/* Description */}
                <p className="text-komel-secondary mb-6 leading-relaxed">
                  {selectedRef.shortDescription}
                </p>

                {/* Services */}
                {selectedRef.services && selectedRef.services.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-heading font-semibold text-komel-primary mb-3">Leistungen</h4>
                    <ul className="space-y-2">
                      {selectedRef.services.map((service, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-komel-secondary">
                          <div className="w-1.5 h-1.5 rounded-full bg-komel-accent" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* PDF Link */}
                {selectedRef.pdfUrl && (
                  <a 
                    href={selectedRef.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-komel-primary hover:text-komel-accent transition-colors"
                    data-testid="reference-pdf-link"
                  >
                    <FileText className="w-5 h-5" />
                    <span className="font-medium">Projektdokumentation herunterladen</span>
                  </a>
                )}

                {/* CTA */}
                <div className="mt-8 pt-6 border-t">
                  <Button
                    onClick={() => {
                      setSelectedRef(null);
                      setTimeout(() => scrollToSection("#kontakt"), 300);
                    }}
                    className="w-full bg-komel-accent hover:bg-komel-accent-hover text-white font-semibold py-4 rounded-none"
                    data-testid="modal-contact-cta"
                  >
                    Ähnliches Projekt anfragen
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ReferencesSection;
